import * as Sentry from '@sentry/react'
import {diagnosticsLogger, setLogContext} from '../../Utils/ccpLogger'
import type {ConnectDiagnosticsReport} from './types'
import {
  DIAGNOSTICS_MODULE,
  type DiagnosticsIdentity,
  buildDiagnosticsLogAttributes,
  messageForTier,
  summarizeLatencyEndpoint,
  summarizeMediaEndpoint,
} from './logAttributes'

const applySentryIdentity = (identity: DiagnosticsIdentity) => {
  if (identity.userId || identity.userEmail) {
    Sentry.setUser({
      id: identity.userId || undefined,
      email: identity.userEmail || undefined,
      username: identity.agentName || undefined,
    })
  }

  setLogContext({
    ...(identity.userId ? {userId: identity.userId} : {}),
    ...(identity.userEmail ? {agentEmail: identity.userEmail} : {}),
    ...(identity.agentName ? {agentName: identity.agentName} : {}),
    ...(identity.contactId ? {contactId: identity.contactId} : {}),
    ...(identity.callHeaderId != null ? {callHeaderId: String(identity.callHeaderId)} : {}),
  })
}

const applySentryTags = (report: ConnectDiagnosticsReport, identity: DiagnosticsIdentity) => {
  Sentry.setTag('diagnostics.latency_tier', report.overallTier)
  Sentry.setTag('diagnostics.latency_only_tier', report.latency.overallTier)
  Sentry.setTag('diagnostics.media_ok', String(report.media.success))
  Sentry.setTag('diagnostics.rtc_support', String(report.clientConfig.rtcSupport))
  Sentry.setTag('diagnostics.region', report.regions[0] || 'none')
  Sentry.setTag('diagnostics.environment', report.environment)
  Sentry.setTag('diagnostics.trigger', identity.trigger || 'call-accept')
  if (identity.contactId) Sentry.setTag('diagnostics.contact_id', identity.contactId)
  if (identity.userId) Sentry.setTag('diagnostics.user_id', identity.userId)
  if (identity.userEmail) Sentry.setTag('diagnostics.user_email', identity.userEmail)

  Sentry.setContext('connectDiagnostics', {
    userId: identity.userId,
    userEmail: identity.userEmail,
    contactId: identity.contactId,
    callId: identity.callId,
    callHeaderId: identity.callHeaderId,
    connectInstanceUrl: report.connectInstanceUrl,
    overallTier: report.overallTier,
    latency: report.latency.endpoints.map(summarizeLatencyEndpoint),
    media: report.media.regions.map((r) => ({
      code: r.code,
      success: r.success,
      endpoints: r.endpoints.map(summarizeMediaEndpoint),
    })),
    clientConfig: {
      browserName: report.clientConfig.browserName,
      rtcSupport: report.clientConfig.rtcSupport,
      hasMicrophone: report.clientConfig.hasMicrophone,
      hasMicrophoneAccess: report.clientConfig.hasMicrophoneAccess,
      errorMessages: report.clientConfig.errorMessages,
    },
  })
}

/**
 * Log a full Connect diagnostics report to Sentry Logs (and Issues on failure).
 * Identity fields are top-level log attributes so Logs search works:
 *   module:diagnostics contactId:"<id>"
 */
export const logConnectDiagnosticsToSentry = (
  report: ConnectDiagnosticsReport,
  identity: DiagnosticsIdentity = {}
) => {
  applySentryIdentity(identity)
  applySentryTags(report, identity)

  const attributes = buildDiagnosticsLogAttributes(report, identity)
  const message = messageForTier(report.overallTier)

  diagnosticsLogger.breadcrumb('Connect diagnostics finished', {
    overallTier: report.overallTier,
    latencyTier: report.latency.overallTier,
    mediaOk: report.media.success,
    connectInstanceUrl: report.connectInstanceUrl,
    contactId: attributes.contactId,
    userId: attributes.userId,
  })

  if (report.overallTier === 'failure') {
    diagnosticsLogger.error(message, attributes)
    Sentry.captureMessage(message, {
      level: 'error',
      tags: {
        'diagnostics.latency_tier': report.overallTier,
        'diagnostics.media_ok': String(report.media.success),
        'diagnostics.environment': report.environment,
        'diagnostics.contact_id': attributes.contactId,
        'diagnostics.user_id': attributes.userId,
      },
      extra: attributes,
    })
    return
  }

  if (report.overallTier === 'warning' || report.overallTier === 'caution') {
    diagnosticsLogger.warn(message, attributes)
    return
  }

  diagnosticsLogger.info(message, attributes)
}

/** @deprecated Prefer logConnectDiagnosticsToSentry */
export const logLatencyDiagnosticsToSentry = logConnectDiagnosticsToSentry

export {DIAGNOSTICS_MODULE, DIAGNOSTICS_LOG_MESSAGES} from './logAttributes'
